/**
 * API client for DEX backend communication.
 * All requests automatically include X-API-Key header.
 * Includes automatic retry with exponential backoff for 404 responses
 * (Carta service worker warm-up) and page-level error banner for
 * unrecoverable connection/server errors.
 */

import { showErrorBanner, hideErrorBanner } from '../ui/components/error-banner.js';

// API base URL — derived from current pathname to support reverse proxy prefixes.
// Carta (/service/cartatest/dex/render/): /service/cartatest/dex/render/api
// CloudFront or localhost (/): /api
// Cached after first successful resolution because window.location.pathname
// can become unreliable in Carta service worker iframes after initialization.
//
// Carta routes both UI assets and API calls through the same /render/ prefix.
// The service worker intercepts fetches and proxies them to the backend.
let _cachedApiBaseUrl = null;

function getApiBaseUrl() {
  if (_cachedApiBaseUrl) {
    return _cachedApiBaseUrl;
  }
  const pathname = window.location?.pathname;
  if (typeof pathname === 'string' && pathname.startsWith('/')) {
    const base = `${pathname.replace(/\/+$/, '')}/api`;
    // Reject pathnames that contain "undefined" or "null" segments — this can
    // happen in Carta service worker iframes during initialization when
    // window.location.pathname is in a transient state.
    if (/\/(undefined|null)(\/|$)/.test(base)) {
      console.warn('[DEX] Suspicious pathname detected:', pathname, '— falling back to /api');
      _cachedApiBaseUrl = '/api';
      return _cachedApiBaseUrl;
    }
    _cachedApiBaseUrl = base;
    console.log('[DEX] API base URL:', _cachedApiBaseUrl, '(from pathname:', pathname + ')');
    return _cachedApiBaseUrl;
  }
  // Fallback when pathname unavailable
  console.warn('[DEX] window.location.pathname unavailable, using /api');
  return '/api';
}

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(status, message, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Current API key (set by auth module)
let currentApiKey = null;

// Recording callback (set by macro recorder)
let recordCallback = null;

// Retry configuration
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;

/**
 * Set the API key for subsequent requests.
 * @param {string|null} key - The API key to use
 */
export function setApiKey(key) {
  currentApiKey = key;
}

/**
 * Get the current API key.
 * @returns {string|null} The current API key
 */
export function getApiKey() {
  return currentApiKey;
}

/**
 * Set the recording callback for macro recording.
 * Called after each API request with request/response details.
 * @param {Function|null} callback - Callback function or null to disable
 */
export function setRecordCallback(callback) {
  recordCallback = callback;
}

/**
 * Fetch with automatic retry on 404 responses.
 * Retries with exponential backoff (500ms, 1000ms, 2000ms) to handle
 * Carta service worker warm-up delays.
 * @param {string} url - Request URL
 * @param {Object} config - Fetch configuration
 * @param {number} retryCount - Current retry attempt (0-based)
 * @returns {Promise<Response>} Fetch response
 */
async function fetchWithRetry(url, config, retryCount = 0) {
  const response = await fetch(url, config);

  if (response.status === 404 && retryCount < MAX_RETRIES) {
    const delay = BASE_DELAY_MS * Math.pow(2, retryCount);
    console.warn(`API 404 on ${url}, retry ${retryCount + 1}/${MAX_RETRIES}...`);
    await new Promise((r) => setTimeout(r, delay));
    return fetchWithRetry(url, config, retryCount + 1);
  }

  return response;
}

/**
 * Status codes that represent legitimate application errors.
 * These are NOT retried and do NOT trigger the connection error banner.
 */
const NON_BANNER_STATUSES = new Set([400, 401, 403, 409]);

/**
 * Make an authenticated API request.
 * @param {string} method - HTTP method
 * @param {string} path - API path (e.g., '/admin/parties')
 * @param {Object|null} body - Request body (will be JSON stringified)
 * @returns {Promise<Object>} Response data
 * @throws {ApiError} On non-2xx response
 */
async function apiRequest(method, path, body = null) {
  const headers = {};

  // Add API key if set
  if (currentApiKey) {
    headers['X-API-Key'] = currentApiKey;
  }

  const config = {
    method,
    headers
  };

  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    // Intentionally omit Content-Type: application/json. Carta's service worker
    // re-serializes JSON bodies (JSON.stringify(JSON.parse(text))) when it detects
    // that Content-Type, which corrupts encrypted payloads. Without the header,
    // the browser defaults to text/plain and the body passes through untouched.
    // Our Lambda handlers parse event.body with json.loads() regardless of Content-Type.
    config.body = JSON.stringify(body);
  }

  // Defensive guard: reject paths containing literal "undefined" or "null" segments.
  // This catches URL construction bugs where encodeURIComponent(undefined) produces
  // the literal string "undefined" in the path.
  if (/\/(undefined|null)(\/|$|\?)/.test(path)) {
    console.warn('[DEX] API path contains invalid segment:', path);
    throw new ApiError(0, 'Invalid API path: contains undefined or null segment');
  }

  let url = `${getApiBaseUrl()}${path}`;

  // Second guard: check the full URL (base + path) for "undefined" or "null" segments.
  // Catches cases where getApiBaseUrl() itself produces a bad prefix (e.g., if
  // window.location.pathname was corrupted by Carta service worker).
  if (/\/(undefined|null)(\/|$|\?)/.test(url)) {
    console.error('[DEX] Full API URL contains invalid segment:', url, '(base:', getApiBaseUrl(), 'path:', path, ')');
    throw new ApiError(0, 'Invalid API URL: contains undefined or null segment');
  }

  // Append API key as query parameter (Carta proxy doesn't forward custom headers)
  if (currentApiKey) {
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}api_key=${encodeURIComponent(currentApiKey)}`;
  }

  try {
    const response = await fetchWithRetry(url, config);

    // Parse response body (may be empty for some responses)
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const errorMessage = data?.error || `Request failed with status ${response.status}`;

      // Call record callback for failed request
      if (recordCallback) {
        try {
          recordCallback({
            method,
            path,
            body,
            status: response.status,
            success: false,
            error: data?.error || errorMessage
          });
        } catch (e) {
          console.error('Recording callback error:', e);
        }
      }

      // Show banner for connection/server errors (404 after retry exhaustion, 500+)
      // Do NOT show banner for legitimate application errors (400, 401, 403, 409)
      if (!NON_BANNER_STATUSES.has(response.status)) {
        console.error(`API error: ${response.status} ${method} ${url}`);
        showErrorBanner('Connection issue \u2014 please refresh the page');
      }

      throw new ApiError(response.status, errorMessage, data);
    }

    // Successful response — auto-clear any previous connection error banner
    hideErrorBanner();

    // Call record callback for successful request
    if (recordCallback) {
      try {
        recordCallback({
          method,
          path,
          body,
          status: response.status,
          success: true,
          error: null
        });
      } catch (e) {
        console.error('Recording callback error:', e);
      }
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Network or other error — show banner for connection issues
    console.error(`Network error: ${method} ${url}:`, error.message);
    showErrorBanner('Connection issue \u2014 please refresh the page');
    throw new ApiError(0, error.message || 'Network error');
  }
}

/**
 * API helper methods.
 */
export const api = {
  /**
   * GET request
   * @param {string} path - API path
   * @returns {Promise<Object>} Response data
   */
  get: (path) => apiRequest('GET', path),

  /**
   * POST request
   * @param {string} path - API path
   * @param {Object} body - Request body
   * @returns {Promise<Object>} Response data
   */
  post: (path, body) => apiRequest('POST', path, body),

  /**
   * PUT request
   * @param {string} path - API path
   * @param {Object} body - Request body
   * @returns {Promise<Object>} Response data
   */
  put: (path, body) => apiRequest('PUT', path, body),

  /**
   * DELETE request
   * @param {string} path - API path
   * @returns {Promise<Object>} Response data
   */
  delete: (path) => apiRequest('DELETE', path)
};
